import React, { useCallback, useEffect, useState } from 'react';
import * as styles from './MobileFilters.module.scss';
import { FilterItem, FilterProps} from '../../typings';
import { FilterTypes } from '../Filters/Filters';
import { fetchfilters } from '../../services/common.service';
import { motion, AnimatePresence } from 'framer-motion';
const { __ } = window.wp.i18n;

interface FilterOptions {
  data: FilterProps[];
}

interface Window {
  cg_jobs_es_var?: { pathName ?: string};
  cg_jobs_country_code?: string;
}
const countryCodeinSettings = (window as Window).cg_jobs_country_code;
const cCode = countryCodeinSettings ? countryCodeinSettings : (window as Window).cg_jobs_es_var?.pathName;

export interface SelectedFilters {
  experience_level: Set<string>;
  location: Set<string>;
  brand: Set<string>;
  professional_communities: Set<string>;
  contract_type: Set<string>;
}

interface FilterComponentProps {
  initialFilters: SelectedFilters;
  onFiltersChange: (newFilters: SelectedFilters) => void;
  totalResult: number; 
  settingFilters?: FilterTypes;
  keyword?: string;
  reset?: boolean;
}

export const MobileFilters: React.FC<FilterComponentProps> = React.memo(({
  initialFilters,
  onFiltersChange,
  totalResult,
  settingFilters,
  keyword,
  reset
}) =>{
  const [filterOptions, setFilterOptions] = useState<FilterProps[] | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(initialFilters);
  const [selectedFilterType, setSelectedFilterType] = useState<FilterTypes>();
  const [loading, setLoading] = useState<boolean>(true);
  const [experience_level, setExperience_level] = useState<FilterItem[]>([]);
  const [professional_communities, setProfessional_communities] = useState<FilterItem[]>([]);
  const [location, setLocation] = useState<FilterItem[]>([]);
  const [contract_type, setContract_type] = useState<FilterItem[]>([]);
  const [brand, setBrand] = useState<FilterItem[]>([]);
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>();
  const [loadingFilters, setLoadingFilters] = useState<boolean>(false);
  const [totalResultLoading, setTotalResultLoading] = useState<boolean>(true);
  const [hasActiveFilters, setHasActiveFilters] = useState<boolean>(Object.values(selectedFilters).some(set => set.size > 0));
  const [visibleFilters, setVisibleFilters] = useState<{ [key in FilterTypes]: number }>({
    location: 10,
    brand: 10,
    professional_communities: 10,
    contract_type: 10,
    experience_level: 10
  });

  const [showMoreFilters, setShowMoreFilters] = useState<{ [key in FilterTypes]?: boolean }>({
    location: false,
    brand: false,
    professional_communities: false,
    contract_type: false,
  });

  const showFilters = settingFilters ? settingFilters : ['location', 'professional_communities', 'contract_type', 'experience_level', 'brand']

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        setLoadingFilters(true);
        const cleanedFilters = Object.fromEntries(
          Object.entries(selectedFilters).filter(([_, value]) => value instanceof Set ? value.size > 0 : Boolean(value))
        );

        const response = await fetchfilters({filters: Object.fromEntries(Object.entries(cleanedFilters).map(([key, value]) => [key, Array.from(value)])), countryCode: cCode, filter: selectedFilterType, search: keyword});
        const data: FilterOptions = response;

        // Fix the order of Filter Taxonomy
        const orderedTypes = ['experience_level', 'professional_communities', 'location', 'contract_type', 'brand'];

        const orderedData = orderedTypes.map(type => {
          return data.data.find(item => item.type === type) as FilterProps;
        }).filter(Boolean) as FilterProps[];

        setFilterOptions(orderedData);
        setLoading(false);
        setLoadingFilters(false);
      } catch (error) {
        console.error('Error fetching filter options:', error);
        setLoading(false);
      }
    };

    fetchFilterOptions();
  }, [selectedFilters, keyword]);

  useEffect(() => {
    if(reset && Object.keys(selectedFilters).length !== 0 ) document.getElementById('resetMobileFilters')?.click();
  }, [reset])

  useEffect(() => {
    setSelectedFilters(initialFilters);
  }, [initialFilters]);

  useEffect(() => {
    if (!loadingFilters && totalResult !== undefined) {
      setTotalResultLoading(false); // Set loading state to false when totalResult is available
    }
  }, [loadingFilters, totalResult]);

  //Show mobile filters
  useEffect(() => {
    const showFilters = () => {
        
        if (window.matchMedia("(min-width: 1024px)").matches) {
            setShowMobileFilters(false);
        } else {
            setShowMobileFilters(true);
        }
    };

    // Set the initial placeholder
    showFilters();

    // Add an event listener for window resize
    window.addEventListener('resize', showFilters);

    // Cleanup event listener on component unmount
    return () => window.removeEventListener('resize', showFilters);
  }, []);
  
  const handleCheckboxChange = useCallback((type: FilterTypes, value: string) => {    
    setSelectedFilters(prevFilters => {
      const newSelection = new Set(prevFilters[type]);
      if (newSelection.has(value)) {
        newSelection.delete(value);
      } else {
        newSelection.add(value);
      }
      const updatedFilters = { ...prevFilters, [type]: newSelection };
      onFiltersChange(updatedFilters);
      setHasActiveFilters(true);
      return updatedFilters;
    });
  }, []);

  const clearAllFilters = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const clearedFilters: SelectedFilters = {
      experience_level: new Set<string>(),
      location: new Set<string>(),
      brand: new Set<string>(),
      professional_communities: new Set<string>(),
      contract_type: new Set<string>(),
    };

    setSelectedFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    e.preventDefault();
    e.stopPropagation();
  };

  if (loading) {
    return <div>{ __( 'Loading...', 'cg-jobs' ) }</div>;
  }

  if (!filterOptions) {
    return <div>{ __( 'No filter options available....', 'cg-jobs' ) }</div>;
  }
  
  const handleShowMore = (type: FilterTypes) => {
    setShowMoreFilters(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
    setVisibleFilters(prev => ({
      ...prev,
      [type]: prev[type] === 10 ? Number.MAX_SAFE_INTEGER : 10
    }));
  };
 
  if (!showMobileFilters) {
    return null; // Render nothing if showMobileFilters is false
  }

  const canvasStyle = {
    maxHeight: '84vh',
    paddingBottom: hasActiveFilters ? '10.625rem' : '1rem',
  };

  function updateLabel(label:string){
    const updatedLabel = label.charAt(0).toUpperCase() + label.slice(1).replace(/_/g, ' ');
    return updatedLabel.replace(/,(\S)/g, ', $1');
  }

  return (
    <div className="offcanvas offcanvas-bottom slideIn" tabIndex={-1} id="offcanvasBottom" aria-labelledby="offcanvasBottomLabel" style={{height: '90vh', transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out', animation: 'bounce 0.5s ease-in-out', borderRadius: '4px 4px 0 0'}}>
      <div className={styles['filter-container']}>     
        <div className={styles['mobile-filter-header']}>          
          <button type="button" className="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close" onClick={() => {
              if(!selectedFilterType) setHasActiveFilters(false);
            }}></button>
        </div>        
        <div className="offcanvas-body small" style={canvasStyle}>           
          <div className={styles['filter-header']}>
            <div className={styles['filter-title']} id="offcanvasBottomLabel">{ __( 'Filter', 'cg-jobs' ) }</div>
          </div>
          {filterOptions.every(obj => Array.isArray(obj.item) && obj.item.length !== 0) 
            ? filterOptions.map(singleFilter => ( 
              (singleFilter.type && singleFilter.type !== "" && showFilters.includes(singleFilter.type)) &&
                <div className={styles['filter-group']} key={singleFilter.type}>
                    <div className={[styles['filter-header'], styles['mb2']].join(' ')}>
                      <h3>{(() => {
                        switch (singleFilter.type) {
                          case 'brand':
                            return 'Brands';
                          case 'professional_communities':
                            return 'Professions';
                          default:
                            return singleFilter.type.charAt(0).toUpperCase() + singleFilter.type.slice(1).replace(/_/g, ' ');
                        }
                      })()}</h3>
  
                      {singleFilter.item.length > 10 && 
                        <button className={styles['filter-button']} onClick={() => handleShowMore(singleFilter.type as FilterTypes)} aria-expanded={showMoreFilters[singleFilter.type as FilterTypes]}
                        aria-label={showMoreFilters[singleFilter.type as FilterTypes] ? `${ __( 'Collapse', 'cg-jobs' ) }` : `${ __( 'Expand', 'cg-jobs' ) }`} >
                          {showMoreFilters[singleFilter.type as FilterTypes] ? `${ __( 'Show less', 'cg-jobs' ) }` : `${ __( 'Show more', 'cg-jobs' ) }`}
                        </button>
                      }                    
                    </div>
                    <AnimatePresence>
                      <motion.ul 
                        className={styles['filter-list']} 
                        id={`filter-list-${singleFilter.type}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >                    
                        {singleFilter.item && singleFilter.item.sort((a,b) => b.count - a.count).slice(0, visibleFilters[singleFilter.type as FilterTypes]).map(childItem => (
                          <motion.li 
                            key={childItem.value} 
                            className={childItem.count === 0 ? styles['no-job'] : ''}
                            layout 
                            transition={{ type: "spring", stiffness: 1200, damping: 200 }}
                          >
                          <input 
                            type="checkbox"
                            id={`${childItem.value}-${singleFilter.type}`} 
                            checked={Array.from(selectedFilters[singleFilter.type as FilterTypes]).flatMap(value => value.split(',')).map(value => value).includes(childItem.value)}
                            onChange={() => {
                              setSelectedFilterType(singleFilter.type as FilterTypes);
                              handleCheckboxChange(singleFilter.type as FilterTypes, childItem.value)
                            }}
                          />
                          <label htmlFor={`${childItem.value}-${singleFilter.type}`}>{updateLabel(childItem.value)} ({childItem.count})</label>
                          </motion.li>
                        ))}
                      </motion.ul>
                    </AnimatePresence>
                  </div>
            )) 
            : <div>{ __( 'No filter options available....', 'cg-jobs' ) }</div>
          }
        </div>

        {hasActiveFilters && 
          <div className={styles['mobile-filter-footer']}>   
            {/* <button type="button" className={styles['show-results']} data-bs-dismiss="offcanvas" aria-label="Show results">{loadingFilters === true ? __( 'Loading', 'cg-jobs' ) : <>{__( 'Show', 'cg-jobs' )} {totalResult} {__( 'results', 'cg-jobs' )}</> }</button> */}
            <button type="button" className={styles['show-results']} data-bs-dismiss="offcanvas" aria-label="Show results">
              {totalResultLoading ? __( 'Loading', 'cg-jobs' ) : <>{__( 'Show', 'cg-jobs' )} {totalResult} {__( 'results', 'cg-jobs' )}</>}
            </button>
            <button type="button" aria-label="Clear all filters" id="resetMobileFilters" onClick={(e) => {
              clearAllFilters(e);
              setSelectedFilterType(undefined);
            }}>{ __( 'Reset', 'cg-jobs' ) }</button>            
          </div>   
        }
      </div>
    </div> 
)});